// UserContext.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification,
  signInWithPopup,
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { db, auth, googleProvider } from "../services/firebaseConfig";
import { getUsersFirebase } from "../services/firebaseService";
import { attachAnonymousPayloadToUser } from "../services/quizService";
import { convertAnonymous } from "../services/quizService";

export const UserContext = React.createContext();

export const UserStorage = ({ children }) => {
  const [user, setUser] = React.useState(null);
  const [users, setUsers] = useState([]);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    async function tryAttachAnonymous() {
      if (!user) return;
      console.log(
        "UserContext: detected login, checking for anonymous quiz data..."
      );

      // read possible keys (compatibility)
      const previewToken =
        localStorage.getItem("ibhava_preview_token") ||
        localStorage.getItem("ibahva.quiz.previewToken");
      const rawPayload =
        localStorage.getItem("ibhava_last_quiz_payload") ||
        localStorage.getItem("ibhava_last_quiz");
      const payload = rawPayload ? JSON.parse(rawPayload) : null;

      if (!previewToken && !payload) {
        console.log("UserContext: no anonymous quiz found");
        return;
      }

      try {
        // prefer server-side conversion if previewToken exists
        if (previewToken) {
          try {
            // get firebase idToken if using firebase auth (optional)
            let idToken = null;
            if (
              window.firebase &&
              window.firebase.auth &&
              window.firebase.auth().currentUser
            ) {
              try {
                idToken = await window.firebase.auth().currentUser.getIdToken();
              } catch (e) {
                console.warn("UserContext: could not get firebase idToken", e);
              }
            }

            console.log(
              "UserContext: attempting convertAnonymous with token",
              previewToken
            );
            const resp = await convertAnonymous(previewToken, idToken);
            console.log("UserContext: convertAnonymous response:", resp);

            // if backend returns updated userData or success -> update local user data
            if (!cancelled) {
              // clear local keys
              localStorage.removeItem("ibhava_preview_token");
              localStorage.removeItem("ibahva.quiz.previewToken");
              localStorage.removeItem("ibhava_last_quiz_payload");
              localStorage.removeItem("ibhava_last_quiz");

              // Preferir usar dados retornados pelo backend (resp.userData).
              // Caso não exista, apenas atualize com o que tivemos como fallback.
              if (resp && resp.userData) {
                setUserData(resp.userData);
              } else if (resp && resp.success && resp.updatedUserData) {
                // compatibilidade com formatos distintos de resposta
                setUserData((prev) => ({
                  ...(prev || {}),
                  ...resp.updatedUserData,
                }));
              } else {
                // Se backend não retornou userData, não sobrescrevemos; o efeito de fallback
                // client-side seguirá para tentar anexar o payload.
                // (Nada a fazer aqui.)
              }
            }

            return;
          } catch (err) {
            console.warn(
              "UserContext: convertAnonymous failed, will try client-side fallback",
              err
            );
            // continue to fallback
          }
        }

        // fallback client-side: if we have payload, attach to user via updateUserFirebase
        if (payload && userData) {
          console.log("UserContext: attempting client-side attach of payload", {
            keys: Object.keys(payload),
          });
          try {
            const res = await attachAnonymousPayloadToUser(
              { payload, previewToken: previewToken || null },
              userData
            );

            // cleanup localStorage and update userData from attach result when available
            if (!cancelled) {
              localStorage.removeItem("ibhava_last_quiz_payload");
              localStorage.removeItem("ibhava_last_quiz");
              localStorage.removeItem("ibhava_preview_token");
              localStorage.removeItem("ibahva.quiz.previewToken");

              // If attachAnonymousPayloadToUser returned updated user info, merge it into context
              if (res && res.updatedUserData) {
                setUserData((prev) => ({
                  ...(prev || {}),
                  ...res.updatedUserData,
                }));
              } else {
                // no user data returned — do nothing (userData likely already present)
              }
            }
          } catch (err) {
            console.error("UserContext: fallback attach failed", err);
          }
        }
      } catch (err) {
        console.error("UserContext: unexpected error during attach", err);
      }
    }

    tryAttachAnonymous();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userData]);

  useEffect(() => {
    getUsersFirebase().then(function (result) {
      setUsers(result);
    });
    onAuthStateChanged(auth, (currentUser) => {
      setLoading(true);
      if (currentUser && !userData) {
        setUser(currentUser);
        getDocument("users", currentUser.uid).then(function (result) {
          setUserData(result);
        });
        setLoading(false);
      } else {
        setLoading(false);
      }
    });
  }, [userData]);

  const userRegister = async (displayName, email, password) => {
    try {
      setError(null);
      setLoading(true);

      // Cria o usuário no Auth
      const res = await createUserWithEmailAndPassword(auth, email, password);
      const u = res.user;

      // Atualiza o nome de exibição
      await updateProfile(u, {
        displayName: displayName,
      });

      // Envia verificação de email (apenas fora do emulador)
      if (window.location.hostname !== "localhost") {
        await sendEmailVerification(u)
          .then(() => console.log("Email verification sent!"))
          .catch((err) =>
            console.warn("Erro ao enviar verificação:", err.message)
          );
      }

      // Cria o documento no Firestore
      const userData = {
        id: u.uid,
        displayName,
        email,
        userType: {
          category: "user",
          subType: null,
          status: "active",
          premiumReport: false,
        },
        profilePic: false,
      };

      await setDoc(doc(db, "users", u.uid), userData);

      // setUser(u);
      // setUserData(userData);
      navigate("/admin");
    } catch (err) {
      console.error("Erro ao registrar usuário:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const userLogin = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      const u = await signInWithEmailAndPassword(auth, email, password);
      setUser(u.user);
      setUserData(getDocument("users", u.user.uid));
      navigate("/");
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);
      const googleUser = await signInWithPopup(auth, googleProvider);
      await updateProfile(auth.currentUser, {
        displayName: googleUser.user.displayName,
      });
      let userLogging = "";
      await getDocument("users", auth.currentUser.uid).then(function (result) {
        userLogging = result;
      });
      await setDoc(doc(db, "users", auth.currentUser.uid), {
        id: auth.currentUser.uid,
        displayName: googleUser.user.displayName,
        email: googleUser.user.email,
        userType: userLogging
          ? userLogging.userType
          : {
              category: "user",
              subType: null,
              status: "active",
              premiumReport: false,
            },
        profilePic: googleUser.user.photoURL ? googleUser.user.photoURL : false,
      });
      setUser(googleUser.user);
      await getDocument("users", auth.currentUser.uid).then(function (result) {
        setUserData(result);
      });
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getDocument = async (collection, uid) => {
    const docRef = doc(db, collection, uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      console.log(collection + " loaded");
      return docSnap.data();
    } else {
      // doc.data() will be undefined in this case
      console.log("No such document!");
      return false;
    }
  };

  const userLogout = async () => {
    try {
      await signOut(auth); // aguarda o logout no Firebase
      setUserData(null);
      setUser(null);
      navigate("/");
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    }
  };

  const passwordReset = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      navigate("/");
      alert("Password reset link sent!");
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  return (
    <UserContext.Provider
      value={{
        users,
        setUsers,
        userRegister,
        userLogin,
        user,
        setUser,
        userData,
        setUserData,
        userLogout,
        getDocument,
        loading,
        error,
        passwordReset,
        signInWithGoogle,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
